/**
 * Single hook that resolves initial wizard values for all three pre-fill paths:
 *
 *   1. Adhoc  — no API. Writes ADHOC_PAYLOAD_DEFAULTS into payloadRef.
 *   2. Create — GET /ats_interviews/new. Writes hydratePlanDefaults into payloadRef.
 *   3. Edit   — GET /ats_interviews/:id or /ats_schedules/:id.
 *               Writes hydratePayloadFromApi into payloadRef.
 *
 * payloadRef is fully hydrated for ALL steps before any step form mounts,
 * making create and edit paths symmetric.
 *
 * Replaces: useEditInterviewFetch + useEditPrefill + useInterviewPlanDefaults.
 */
import { useEffect, useRef } from 'react';
import { useGetInterview } from '@/api/useInterviews';
import { useGetSelfSchedule } from '@/api/useSelfSchedule';
import { useShowToast } from '@/hooks/useShowToast';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
} from '../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../config/modeClassifiers';
import { useInterviewPlan } from '../api/useInterviewPlan';
import { ADHOC_PAYLOAD_DEFAULTS } from './uiDefaults';
import { hydratePlanDefaults } from './fromPlan';
import { hydratePayloadFromApi } from './fromEditApi';

interface UseWizardInitialValuesOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  stageId?: number;
  stageActivityId?: number;
  candidateId?: number;
  interviewId?: number;
  scheduleId?: number;
}

interface UseWizardInitialValuesResult {
  payloadRef: React.MutableRefObject<ScheduleInterviewPayload>;
  prefillVersion: number;
  /** Raw API response kept as a prop for behavioral flags
   *  (shared_calendar_enabled, minimum_notice_config, zoom_status, etc.).
   *  Initial VALUES come from payloadRef — not from this object. */
  interviewPlan: Record<string, unknown> | undefined;
  showCreateLoader: boolean;
  showEditLoader: boolean;
  showEditError: boolean;
}

export function useWizardInitialValues({
  mode,
  apiReference,
  stageId,
  stageActivityId,
  candidateId,
  interviewId,
  scheduleId,
}: UseWizardInitialValuesOptions): UseWizardInitialValuesResult {
  const isEdit = apiReference === 'edit';
  const isAdhoc = isAdhocMode(mode);
  const isSelfSchedule = isSelfScheduleMode(mode);

  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  // ── Path 1: Adhoc ──────────────────────────────────────────────────
  // No API call. Seed payloadRef synchronously on first render so the
  // form mounts with the correct blank defaults.
  const adhocInitRef = useRef(false);
  if (isAdhoc && !adhocInitRef.current) {
    adhocInitRef.current = true;
    payloadRef.current = {
      ...ADHOC_PAYLOAD_DEFAULTS,
      details: { ...ADHOC_PAYLOAD_DEFAULTS.details },
    };
  }
  if (!isAdhoc) {
    adhocInitRef.current = false;
  }

  // ── Path 3: Edit — fetch ────────────────────────────────────────────
  const useScheduleGet = isEdit && isSelfSchedule && !!scheduleId;
  const toast = useShowToast();

  const {
    data: interviewFromInterviewsEndpoint,
    isLoading: isLoadingInterview,
    isError: isInterviewLoadError,
    isFetched: isFetchedInterview,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !useScheduleGet && !!interviewId,
  });

  const {
    data: interviewFromSchedulesEndpoint,
    isLoading: isLoadingSchedule,
    isError: isScheduleLoadError,
    isFetched: isFetchedSchedule,
  } = useGetSelfSchedule(scheduleId, {
    enabled: useScheduleGet,
  });

  const existingInterview = useScheduleGet
    ? interviewFromSchedulesEndpoint
    : interviewFromInterviewsEndpoint;
  const isLoadingExisting = useScheduleGet
    ? isLoadingSchedule
    : isLoadingInterview;
  const isLoadError = useScheduleGet
    ? isScheduleLoadError
    : isInterviewLoadError;
  const isFetchedExisting = useScheduleGet
    ? isFetchedSchedule
    : isFetchedInterview;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ── Path 3: Edit — hydrate payloadRef ──────────────────────────────
  const lastHydratedIdRef = useRef<number | null>(null);
  const prefillGenerationRef = useRef(0);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  if (editDataReady && existingInterview.id !== lastHydratedIdRef.current) {
    lastHydratedIdRef.current = existingInterview.id;
    prefillGenerationRef.current += 1;
    payloadRef.current = hydratePayloadFromApi(existingInterview);
  }

  // ── Path 2: Create — fetch plan ────────────────────────────────────
  const planEnabled = !isAdhoc && !isEdit && !!stageId && stageId > 0;

  const { data: planData, isLoading: isLoadingPlan } = useInterviewPlan(
    {
      stageId: stageId ?? 0,
      stageActivityId: stageActivityId ?? undefined,
      contactId: candidateId ?? undefined,
    },
    { enabled: planEnabled }
  );

  // Hydrate payloadRef once when plan resolves so ALL steps have their
  // defaults before any form mounts (gated by showCreateLoader).
  const planHydratedRef = useRef(false);
  if (!isEdit) planHydratedRef.current = false;
  if (!isEdit && !isAdhoc && planData && !planHydratedRef.current) {
    planHydratedRef.current = true;
    payloadRef.current = hydratePlanDefaults(planData, mode);
  }

  // ── Derive interviewPlan for behavioral flags ──────────────────────
  // Edit: use the raw existing interview (feature flags live there).
  // Create: use the raw plan response.
  // Adhoc: undefined (no plan).
  const interviewPlan: Record<string, unknown> | undefined = isEdit
    ? (existingInterview as Record<string, unknown> | undefined)
    : planData;

  return {
    payloadRef,
    prefillVersion: isEdit ? prefillGenerationRef.current : 0,
    interviewPlan,
    showCreateLoader: planEnabled && isLoadingPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
  };
}
